// The hero CTA copy test on the mobile-proof pages (logic and arms: utils/tracking/ctaTest.ts).
// `variant` stays null on the server, while hydrating and for anyone not enrolled, so the
// label is arm "a" there and the data-conversion-variant attribute is left off.
import { CTA_TEST_COPY, CTA_TEST_STORAGE_KEY, assignCtaVariant, ctaExposureEvent, ctaTestEligible, type CtaVariant } from '~/utils/tracking/ctaTest';
import { mobileProofPaths } from '~/data/mobile-app-facts';

export function useCtaTest(path: MaybeRefOrGetter<string>) {
  const { consent } = useCookieConsent();
  const { currentLocaleCode } = useWorkspaceLocale();
  const arm = ref<CtaVariant | null>(null);
  const enrolled = computed(() => mobileProofPaths.has(toValue(path)));

  const enroll = () => {
    if (arm.value || !ctaTestEligible(consent.value?.analytics === true, currentLocaleCode.value, enrolled.value)) return;
    let stored: string | null = null;
    try { stored = window.localStorage.getItem(CTA_TEST_STORAGE_KEY); } catch {}
    const { variant, fresh } = assignCtaVariant(stored, Math.random);
    // Without storage the arm still holds for this page view; it just isn't sticky.
    if (fresh) try { window.localStorage.setItem(CTA_TEST_STORAGE_KEY, variant); } catch {}
    arm.value = variant;
    const { event: name, ...parameters } = ctaExposureEvent(variant, window.location.pathname);
    if (typeof window.gtag === 'function') window.gtag('event', name, parameters);
    else { window.dataLayer ||= []; window.dataLayer.push({ event: name, ...parameters }); }
  };

  // Consent can land after mount (banner choice, cookie read), so watch it. The dynamic
  // alternatives route reuses this instance across slugs: a new page gets its own exposure.
  onMounted(() => {
    watch(() => toValue(path), () => { arm.value = null; enroll(); });
    watch(() => consent.value?.analytics, enroll, { immediate: true });
  });
  const variant = computed(() => (enrolled.value ? arm.value : null));

  return {
    enrolled,
    variant,
    label: computed(() => CTA_TEST_COPY[variant.value ?? 'a']),
  };
}
